import { DrillingMultiAnalysis, useBitmapFont } from '@go-widgets/well-logs-widget';
import inter18ptRegularFntUrl from '@go-widgets/well-logs-widget/dist/assets/fonts/Inter_18pt-Regular.fnt?url';
import inter18ptRegularPngUrl from '@go-widgets/well-logs-widget/dist/assets/fonts/Inter_18pt-Regular.png?url';
import { FullArrowLight as FullIcon, ReduceLight as MinimizeIcon } from '@repo/go-icons';
import { Button, ConfigContext, Loader, Tooltip } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useService } from 'src/packages/di';

import { NoWellChosen } from '../no-well-chosen';
import { WidgetHeader } from '../widget-header/WidgetHeader';
import type { TWidgetRendererOptions } from '../workspace/services/WidgetRendererService';

import { CalculationStatus } from './CalculationStatus';
import type { DrillingMultiAnalysisWidgetEntity } from './DrillingMultiAnalysisWidget.entity';
import styles from './DrillingMultiAnalysisWidget.module.scss';
import { DrillingMultiAnalysisWidgetStore } from './DrillingMultiAnalysisWidget.store';

type WrapperProps = {
  entity: DrillingMultiAnalysisWidgetEntity;
  options: TWidgetRendererOptions;
};

type Props = {
  store: DrillingMultiAnalysisWidgetStore;
  entity: DrillingMultiAnalysisWidgetEntity;
  options: TWidgetRendererOptions;
};

export const DrillingMultiAnalysisWidgetWrapper = observer(function DrillingMultiAnalysisWidgetWrapper({
  entity,
  options,
}: WrapperProps) {
  const [store, setStore] = useState<DrillingMultiAnalysisWidgetStore | null>(null);
  const storeService = useService('widgetStoreService');

  useEffect(() => {
    if (store) return;

    const _store = storeService.getStore<DrillingMultiAnalysisWidgetEntity, DrillingMultiAnalysisWidgetStore>(entity);

    if (_store) {
      setStore(_store);
    } else {
      const newStore = new DrillingMultiAnalysisWidgetStore(entity, options.groupSelectStore, options.wellListEntity);

      storeService.setStore<DrillingMultiAnalysisWidgetEntity, DrillingMultiAnalysisWidgetStore>(entity, newStore);
      setStore(newStore);
    }
  }, [setStore, storeService, entity, options.groupSelectStore, store, options.wellListEntity]);

  if (!store) return null;

  return <DrillingMultiAnalysisWidget store={store} entity={entity} options={options} />;
});

export const DrillingMultiAnalysisWidget = observer(function DrillingMultiAnalysisWidget({
  entity,
  options,
  store,
}: Props) {
  const globalConfig = useContext(ConfigContext);
  const { t } = useTranslation();
  const { effect, setGroupForWidget } = store;
  const { wellId, isFullscreen, state, setFullscreen, wellboreName, corridorsCalculationStatus } = entity;

  const { loaded: bitmapFontLoaded, error: bitmapFontError } = useBitmapFont({
    fontName: 'Inter_18pt-Regular',
    fntUrl: inter18ptRegularFntUrl,
    pngUrl: inter18ptRegularPngUrl,
  });

  useEffect(effect, [effect]);

  const directories = useService('directories');
  const wells = directories.getObject('GOdashboard_Well');
  const wellName = wells?.find((item) => item.id === wellId)?.data.name ?? wellId;

  return (
    <div className={styles.widgetWrapper}>
      <WidgetHeader
        entity={entity}
        onWidgetChangeGroup={setGroupForWidget}
        groupSelectStore={options.groupSelectStore}
        widgetName={entity.title}
        wellName={wellName}
        wellboreName={wellboreName}
        onWidgetDelete={() => options.onWidgetDelete(entity)}
        progress={store.progress}
        showCloseButton={!isFullscreen}
        draggable={!isFullscreen}
        renderAdditionalButtons={() => {
          return (
            <>
              {corridorsCalculationStatus && corridorsCalculationStatus !== 'COMPLETED' && (
                <CalculationStatus status={corridorsCalculationStatus} />
              )}
              {!isFullscreen && (
                <Tooltip title={t('common:Widget.fullscreen')} delay={400}>
                  <Button
                    onClick={() => setFullscreen(true)}
                    variant="flat"
                    icon={<FullIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
              {isFullscreen && (
                <Tooltip title={t('common:Widget.collapse')} delay={400}>
                  <Button
                    onClick={() => store.exitFullScreen(globalConfig)}
                    variant="flat"
                    icon={<MinimizeIcon className={styles.icon} />}
                    disabled={store.isExitFullscreenFetching}
                    className={styles.button}
                  />
                </Tooltip>
              )}
            </>
          );
        }}
      />
      {state && wellId && bitmapFontLoaded && !bitmapFontError ? (
        <div className={styles.wrapper}>
          <DrillingMultiAnalysis widgetState={state} />
        </div>
      ) : store.isWellListLoading ? (
        <div className={styles.wrapper}>
          <Loader size="l" />
        </div>
      ) : (
        <NoWellChosen />
      )}
    </div>
  );
});
